import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess } from "Components/constants/Common";
import {
  emailRegex,
  InputPlaceHolder,
  numberRegex,
  SelectPlaceHolder,
  validationMessages,
  zipcodeRegex,
} from "Components/constants/validation";
import { BaseSelect } from "Components/Base/BaseSelect";
import { listOfCity, listOfCountry, listOfState } from "Api/common";
import {
  AddressLabel,
  CityLabel,
  CountryLabel,
  EmailLabel,
  PhoneNumberLabel,
  PostalCodeLabel,
  StateLabel,
  StreetLabel,
} from "Components/constants/Staff";
import {
  cancelLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import {
  EDIT_STORE_LABEL,
  NEW_STORE_LABEL,
  STORE_NAME,
} from "Components/constants/Store";
import { createStore, editStore, viewStore } from "Api/Store";

const validationSchema = yup.object({
  storeName: yup.string().required(validationMessages.required(STORE_NAME)),
  phoneNumber: yup
    .string()
    .required(validationMessages.required(PhoneNumberLabel))
    .matches(
      numberRegex,
      validationMessages.contactLength(PhoneNumberLabel, 10)
    ),
  email: yup
    .string()
    .required(validationMessages.required(EmailLabel))
    .matches(emailRegex, validationMessages.format(EmailLabel)),
  shippingPostalCode: yup
    .string()
    .matches(
      zipcodeRegex,
      validationMessages.contactLength(PostalCodeLabel, 6)
    ),
});

const AddStore = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  document.title = id ? EDIT_STORE_LABEL : NEW_STORE_LABEL;
  const [loader, setLoader] = useState(false);
  const [viewStoreData, setViewStoreData] = useState<any>([]);
  const [btnLoader, setBtnLoader] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [city, setCity] = useState([]);

  const fetchCountry = () => {
    setLoader(true);
    let data;
    const payload = {};
    listOfCountry(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          data = res?.data;
          setCountries(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.country_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchState = (id: number) => {
    setLoader(true);
    let data;
    const payload = {
      condition: {
        country_id: id,
      },
    };
    listOfState(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setStates(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.state_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err: any) => {
        setStates([]);
        fetchCity(null);
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchCity = (id: any) => {
    setLoader(true);
    let data;
    const payload = {
      condition: {
        state_id: id,
      },
    };
    listOfCity(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setCity(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.city_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        setCity([]);
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      storeName: id ? viewStoreData?.store_name : "",
      phoneNumber: id ? viewStoreData?.contact_no || "" : "",
      email: id ? viewStoreData?.email || "" : "",
      shippingStreet: id ? viewStoreData?.storeAddress?.address || "" : "",
      shippingCountry: id ? viewStoreData?.storeAddress?.country?.id : "",
      shippingState: id ? viewStoreData?.storeAddress?.state?.id : "",
      shippingCity: id ? viewStoreData?.storeAddress?.city?.id : "",
      shippingPostalCode: id
        ? viewStoreData?.storeAddress?.postal_code || ""
        : "",
    },
    validationSchema,
    onSubmit: (values) => {
      setBtnLoader(true);
      const payload = {
        store_name: values.storeName,
        contact_no: values.phoneNumber,
        email: values.email,
        storeAddress: {
          address: values.shippingStreet || null,
          country_id: values.shippingCountry || null,
          state_id: values.shippingState || null,
          city_id: values.shippingCity || null,
          postal_code: Number(values.shippingPostalCode) || null,
        },
      };
      if (id) {
        editStore(Number(id), payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              navigate("/store");
            } else {
              toast.error(res?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      } else {
        createStore(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              navigate("/store");
            } else {
              toast.error(res?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      }
    },
  });

  useEffect(() => {
    if (formik.isSubmitting && Object.keys(formik.errors).length > 0) {
      const firstErrorKey = Object.keys(formik.errors)[0];

      if (firstErrorKey) {
        const errorElement: HTMLElement | null = document.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}`
        );

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          if (
            errorElement.tagName === "INPUT" ||
            errorElement.tagName === "SELECT"
          ) {
            errorElement.focus();
          } else {
            const selectControl = errorElement.closest(".select__control");
            if (selectControl) {
              selectControl.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              const selectInput = selectControl.querySelector("input, select");
              if (selectInput) {
                (selectInput as HTMLElement).focus();
              }
            }
          }
        }
      }
    }
  }, [formik.isSubmitting, formik.errors]);

  const fetchStoreData = () => {
    if (id) {
      setLoader(true);
      viewStore(Number(id))
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const storeData = res?.data;
            setViewStoreData(storeData);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  useEffect(() => {
    if (viewStoreData?.storeAddress?.country?.id) {
      fetchState(Number(viewStoreData?.storeAddress?.country?.id));
    }
    if (viewStoreData?.storeAddress?.state?.id) {
      fetchCity(Number(viewStoreData?.storeAddress?.state?.id));
    }
  }, [viewStoreData]);

  useEffect(() => {
    if (id) {
      fetchStoreData();
    }
    fetchCountry();
  }, []);

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">
            {id ? EDIT_STORE_LABEL : NEW_STORE_LABEL}
          </span>
          <button className="btn btn-danger" onClick={() => navigate("/store")}>
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <form onSubmit={formik.handleSubmit} className="mt-2 mb-4">
              <div className="row">
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="storeName"
                    label={STORE_NAME}
                    placeholder={InputPlaceHolder(STORE_NAME)}
                    value={formik.values.storeName}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.storeName}
                    touched={formik.touched.storeName}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="phoneNumber"
                    type="text"
                    label={PhoneNumberLabel}
                    placeholder={InputPlaceHolder(PhoneNumberLabel)}
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.phoneNumber}
                    maxLength={10}
                    touched={formik.touched.phoneNumber}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="email"
                    label={EmailLabel}
                    placeholder={InputPlaceHolder(EmailLabel)}
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.email}
                    touched={formik.touched.email}
                    required
                  />
                </div>
              </div>
              <div className="row mt-4">
                <div className="col-12 fs-5">{AddressLabel}</div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="shippingStreet"
                    type="textarea"
                    label={StreetLabel}
                    placeholder={InputPlaceHolder(StreetLabel)}
                    value={formik.values.shippingStreet}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingStreet}
                    touched={formik.touched.shippingStreet}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingCountry"
                    label={CountryLabel}
                    options={countries}
                    value={formik.values.shippingCountry}
                    placeholder={SelectPlaceHolder(CountryLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchState(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingCountry}
                    touched={formik.touched.shippingCountry}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingState"
                    label={StateLabel}
                    options={states}
                    value={formik.values.shippingState}
                    placeholder={SelectPlaceHolder(StateLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchCity(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingState}
                    touched={formik.touched.shippingState}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingCity"
                    label={CityLabel}
                    options={city}
                    value={formik.values.shippingCity}
                    placeholder={SelectPlaceHolder(CityLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingCity}
                    touched={formik.touched.shippingCity}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="shippingPostalCode"
                    type="text"
                    maxLength={6}
                    label={PostalCodeLabel}
                    placeholder={InputPlaceHolder(PostalCodeLabel)}
                    value={formik.values.shippingPostalCode}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingPostalCode}
                    touched={formik.touched.shippingPostalCode}
                  />
                </div>
              </div>

              <div className="row mt-3 d-flex justify-content-end">
                <div className="col-auto d-flex justify-content-end gap-2 text-end">
                  <BaseButton
                    type="submit"
                    label={id ? updateLabel : submitLabel}
                    color="success"
                    loader={btnLoader}
                    disable={btnLoader}
                  />
                  <BaseButton
                    type="button"
                    label={cancelLabel}
                    onClick={() => navigate("/store")}
                    color="danger"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStore;
